<script setup lang="ts">
import DecorShape, { type DecorShapeKind } from "./DecorShape.vue";

withDefaults(defineProps<{ compact?: boolean }>(), { compact: false });

// Preserve the website artwork's 1440 × 88 coordinate system.
const stage = { width: 1440, height: 88 };
interface Piece {
  shape: DecorShapeKind;
  color: string;
  size: number;
  left: number;
  top: number;
  rotate?: number;
}
const pieces: Piece[] = [
  { shape: "dot", color: "yellow", size: 10, left: 90, top: 30 },
  { shape: "triangle-up", color: "green", size: 14, left: 110, top: 50 },
  { shape: "star", color: "yellow", size: 28, left: 150, top: 26 },
  { shape: "dot", color: "blue", size: 10, left: 252, top: 46 },
  { shape: "triangle-up", color: "green", size: 16, left: 352, top: 30 },
  { shape: "dot", color: "orange", size: 12, left: 460, top: 48 },
  { shape: "ring", color: "ink", size: 14, left: 568, top: 28 },
  {
    shape: "triangle-right",
    color: "blue",
    size: 16,
    left: 690,
    top: 44,
    rotate: 12,
  },
  { shape: "star", color: "yellow", size: 16, left: 806, top: 28 },
  { shape: "dot", color: "ink", size: 8, left: 910, top: 48 },
  { shape: "star", color: "orange", size: 18, left: 1016, top: 30 },
  { shape: "triangle-up", color: "green", size: 14, left: 1124, top: 46 },
  { shape: "dot", color: "blue", size: 10, left: 1236, top: 28 },
  { shape: "star", color: "yellow", size: 18, left: 1330, top: 44 },
];

function pct(value: number, basis: number): string {
  return `${((value / basis) * 100).toFixed(4)}%`;
}

function pieceStyle(piece: Piece): Record<string, string> {
  return {
    left: pct(piece.left, stage.width),
    top: pct(piece.top, stage.height),
    width: pct(piece.size, stage.width),
    height: pct(piece.size, stage.height),
    ...(piece.rotate !== undefined ? { rotate: `${piece.rotate}deg` } : {}),
  };
}
</script>

<template>
  <div
    aria-hidden="true"
    class="pointer-events-none relative mx-auto w-full max-w-decor-strip overflow-hidden"
  >
    <!-- Compact surfaces crop the artwork instead of shrinking it indefinitely. -->
    <div
      class="relative left-1/2 aspect-decor-strip w-full -translate-x-1/2"
      :class="{ 'min-w-decor-strip': compact }"
    >
      <DecorShape
        v-for="(piece, index) in pieces"
        :key="index"
        :shape="piece.shape"
        :color="`var(--color-decor-${piece.color})`"
        :styles="pieceStyle(piece)"
      />
    </div>
  </div>
</template>
